import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Zap, FileText, Users, CheckCircle, ArrowRight, 
  Copy, Replace, Download
} from 'lucide-react';
import PublicHeader from '@/components/PublicHeader';

export default function InstantMailMergePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader breadcrumbs={[{ label: 'Features', link: '/how-it-works' }, { label: 'Instant Mail Merge' }]} />

      <div className="pt-32">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-violet-600 to-indigo-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-white">
            <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
              <Zap className="w-10 h-10" />
            </div>
            <h1 className="text-5xl font-bold mb-4">Instant Mail Merge</h1>
            <p className="text-xl text-violet-100 max-w-3xl mx-auto">
              Generate hundreds of personalized documents in seconds. Replace placeholders with real data using intelligent mail merge.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">How It Works</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Transform your templates into personalized documents effortlessly
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                1
              </div>
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Select Template</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Choose your template with predefined fields. Fields are automatically detected from your Word document.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                2
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Fill Data</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Enter your data once or import from CSV. Each field is replaced with your actual values automatically.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8">
              <div className="w-12 h-12 rounded-xl bg-violet-100 text-violet-600 font-bold text-xl flex items-center justify-center mb-6">
                3
              </div>
              <div className="flex items-center gap-3 mb-4">
                <Download className="w-6 h-6 text-violet-600" />
                <h3 className="text-xl font-bold text-slate-900">Export</h3>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Download your personalized documents as DOCX or PDF. Formatting and styling perfectly preserved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Example */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Smart Replacement Engine</h2>
              <p className="text-lg text-slate-600 mb-6">
                Our intelligent mail merge engine replaces all placeholders with actual data while maintaining your document's original formatting.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Replace className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Batch Processing</p>
                    <p className="text-sm text-slate-600">Generate multiple documents from a single template</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Copy className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Format Preservation</p>
                    <p className="text-sm text-slate-600">Fonts, colors, tables, and images remain intact</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-slate-900">Error-Free</p>
                    <p className="text-sm text-slate-600">No typos or manual data entry mistakes</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-900 rounded-2xl p-8 text-white">
              <div className="mb-6">
                <p className="text-sm text-slate-400 mb-2">Template:</p>
                <p className="text-green-400">Dear <span className="text-yellow-400">{'{client_name}'}</span>,</p>
                <p className="text-green-400 mt-2">Your invoice for <span className="text-yellow-400">{'{service}'}</span></p>
                <p className="text-green-400">Amount: <span className="text-yellow-400">{'{amount}'}</span></p>
              </div>
              
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-slate-700"></div>
                <ArrowRight className="w-5 h-5 text-slate-500" />
                <div className="flex-1 h-px bg-slate-700"></div>
              </div>
              
              <div>
                <p className="text-sm text-slate-400 mb-2">Generated Document:</p>
                <p className="text-white">Dear <span className="text-blue-400">John Smith</span>,</p>
                <p className="text-white mt-2">Your invoice for <span className="text-blue-400">Legal Consultation</span></p>
                <p className="text-white">Amount: <span className="text-blue-400">$500.00</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Perfect For</h2>
            <p className="text-lg text-slate-600">
              Automate any document workflow that requires personalization
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Invoices & Receipts',
              'Contracts & Agreements',
              'Legal Documents',
              'HR Letters & Offers',
              'Client Communications',
              'Reports & Summaries'
            ].map((useCase, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <p className="font-medium text-slate-900">{useCase}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Start Merging Today</h2>
          <p className="text-lg text-slate-600 mb-8">
            Join thousands of users automating their document workflows
          </p>
          <Button
            onClick={() => navigate('/auth')}
            size="lg"
            className="h-14 px-10 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-lg font-bold"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
      </div>
    </div>
  );
}
